import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader2, Plus, Trash2, GripVertical, Eye, EyeOff, MapPin, AlertCircle, CheckCircle, RefreshCw, Image as ImageIcon, Database } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ImageUploader from '@/components/ImageUploader';
import VideoUploader from '@/components/VideoUploader';
import RichTextEditor from '@/components/RichTextEditor';

// Componente per la sezione Manutenzione
function MaintenanceSection() {
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const validateImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/images/validate');
      setValidation(res.data);
      if (res.data.broken > 0) {
        toast.warning(`Trovate ${res.data.broken} immagini con problemi`);
      } else {
        toast.success('Tutte le immagini sono valide');
      }
    } catch (error) {
      toast.error('Errore durante la validazione');
    } finally {
      setLoading(false);
    }
  }, []);

  const migrateImages = async () => {
    setMigrating(true);
    try {
      const res = await api.post('/admin/images/migrate');
      toast.success(`Migrate ${res.data.migrated} immagini. Saltate: ${res.data.skipped}. Errori: ${res.data.errors}`);
      // Refresh validation
      await validateImages();
    } catch (error) {
      toast.error('Errore durante la migrazione');
    } finally {
      setMigrating(false);
    }
  };

  const cleanupBroken = async () => {
    if (!validation || validation.broken === 0) {
      toast.info('Nessuna immagine da pulire');
      return;
    }
    
    if (!window.confirm(`Eliminare ${validation.broken} immagini rotte?`)) return;
    
    setCleaning(true);
    try {
      const res = await api.delete('/admin/images/broken');
      toast.success(`Eliminate ${res.data.deleted} immagini rotte`);
      // Refresh validation
      await validateImages();
    } catch (error) {
      toast.error('Errore durante la pulizia');
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => {
    validateImages();
  }, [validateImages]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database size={20} />
          Manutenzione Immagini
        </CardTitle>
        <CardDescription>
          Strumenti per gestire e riparare le immagini del sito
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status */}
        {validation && (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-muted rounded-sm text-center">
              <ImageIcon size={24} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-2xl font-bold">{validation.total}</p>
              <p className="text-sm text-muted-foreground">Totale immagini</p>
            </div>
            <div className="p-4 bg-green-50 rounded-sm text-center">
              <CheckCircle size={24} className="mx-auto mb-2 text-green-600" />
              <p className="text-2xl font-bold text-green-700">{validation.valid}</p>
              <p className="text-sm text-green-600">Valide</p>
            </div>
            <div className={`p-4 rounded-sm text-center ${validation.broken > 0 ? 'bg-red-50' : 'bg-muted'}`}>
              <AlertCircle size={24} className={`mx-auto mb-2 ${validation.broken > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
              <p className={`text-2xl font-bold ${validation.broken > 0 ? 'text-red-700' : ''}`}>{validation.broken}</p>
              <p className={`text-sm ${validation.broken > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>Rotte</p>
            </div>
          </div>
        )}

        {/* Broken images list */}
        {validation?.broken_images?.length > 0 && (
          <div className="border border-red-200 rounded-sm overflow-hidden">
            <div className="bg-red-50 px-4 py-2 border-b border-red-200">
              <span className="text-sm font-medium text-red-800">Immagini con problemi</span>
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-red-100">
              {validation.broken_images.map((img, idx) => (
                <div key={idx} className="px-4 py-2 text-sm">
                  <p className="font-medium text-red-800">{img.filename || img.id}</p>
                  <p className="text-xs text-red-600">
                    Problemi: {img.issues?.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={validateImages}
            disabled={loading}
          >
            {loading ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <RefreshCw size={16} className="mr-2" />
            )}
            Verifica Immagini
          </Button>
          
          <Button
            variant="outline"
            onClick={migrateImages}
            disabled={migrating}
          >
            {migrating ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Database size={16} className="mr-2" />
            )}
            Migra a MongoDB
          </Button>
          
          {validation?.broken > 0 && (
            <Button
              variant="destructive"
              onClick={cleanupBroken}
              disabled={cleaning}
            >
              {cleaning ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Trash2 size={16} className="mr-2" />
              )}
              Elimina Rotte ({validation.broken})
            </Button>
          )}
        </div>

        {/* Info */}
        <div className="p-4 bg-blue-50 rounded-sm">
          <h4 className="font-medium text-blue-800 mb-2">Informazioni</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Verifica:</strong> Controlla che tutte le immagini abbiano i dati necessari</li>
            <li>• <strong>Migra:</strong> Copia le immagini dal filesystem al database per persistenza</li>
            <li>• <strong>Elimina rotte:</strong> Rimuove dal database le immagini incomplete</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// Componente per stat item sortabile
function SortableStatItem({ stat, onUpdate, onRemove, onToggleVisibility }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 border border-border rounded-sm bg-background ${!stat.is_visible ? 'opacity-60' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
      >
        <GripVertical size={18} />
      </button>
      
      <div className="flex-1 grid grid-cols-3 gap-2">
        <Input
          value={stat.value}
          onChange={(e) => onUpdate(stat.id, 'value', e.target.value)}
          placeholder="500"
          className="text-center font-bold"
        />
        <Input
          value={stat.suffix}
          onChange={(e) => onUpdate(stat.id, 'suffix', e.target.value)}
          placeholder="+"
          className="text-center"
        />
        <Input
          value={stat.label}
          onChange={(e) => onUpdate(stat.id, 'label', e.target.value)}
          placeholder="Progetti"
        />
      </div>
      
      <button
        onClick={() => onToggleVisibility(stat.id)}
        className="text-muted-foreground hover:text-foreground"
        title={stat.is_visible ? 'Nascondi' : 'Mostra'}
      >
        {stat.is_visible ? <Eye size={18} /> : <EyeOff size={18} />}
      </button>
      
      <button
        onClick={() => onRemove(stat.id)}
        className="text-destructive hover:text-destructive/80"
      >
        <Trash2 size={18} />
      </button>
    </div>
  );
}

// Componente per quick link sortabile
function SortableQuickLink({ link, index, onUpdate, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.id || `link-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 border border-border rounded-sm"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground"
      >
        <GripVertical size={16} />
      </button>
      <Input
        value={link.label}
        onChange={(e) => onUpdate(index, 'label', e.target.value)}
        placeholder="Etichetta"
        className="flex-1"
      />
      <Input
        value={link.url}
        onChange={(e) => onUpdate(index, 'url', e.target.value)}
        placeholder="/pagina"
        className="flex-1"
      />
      <button
        onClick={() => onRemove(index)}
        className="text-destructive hover:text-destructive/80"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/admin/settings');
      // Assicura che stats e footer esistano
      const data = {
        ...response.data,
        stats: response.data.stats || [],
        footer: response.data.footer || {
          copyright_text: '© {year} Andrea Caruso. Tutti i diritti riservati.',
          show_social: true,
          show_contact_info: true,
          show_quick_links: true,
          quick_links: [],
          additional_text: ''
        }
      };
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name, value) => {
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (field, imageData) => {
    if (imageData) {
      setSettings(prev => ({
        ...prev,
        [field]: imageData.url,
        [`${field}_thumbnail`]: imageData.thumbnail_url
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [field]: null,
        [`${field}_thumbnail`]: null
      }));
    }
  };

  const handleRichTextChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Stats handlers
  const addStat = () => {
    const newStat = {
      id: `stat-${Date.now()}`,
      value: '',
      suffix: '+',
      label: '',
      order: settings.stats?.length || 0,
      is_visible: true
    };
    setSettings(prev => ({
      ...prev,
      stats: [...(prev.stats || []), newStat]
    }));
  };

  const updateStat = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      stats: prev.stats.map(s => s.id === id ? { ...s, [field]: value } : s)
    }));
  };

  const removeStat = (id) => {
    setSettings(prev => ({
      ...prev,
      stats: prev.stats.filter(s => s.id !== id)
    }));
  };

  const toggleStatVisibility = (id) => {
    setSettings(prev => ({
      ...prev,
      stats: prev.stats.map(s => s.id === id ? { ...s, is_visible: !s.is_visible } : s)
    }));
  };

  const handleStatsDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSettings(prev => {
        const oldIndex = prev.stats.findIndex(s => s.id === active.id);
        const newIndex = prev.stats.findIndex(s => s.id === over.id);
        const newStats = arrayMove(prev.stats, oldIndex, newIndex).map((s, i) => ({ ...s, order: i }));
        return { ...prev, stats: newStats };
      });
    }
  };

  // FAQ handlers
  const addFaq = () => {
    setSettings(prev => ({
      ...prev,
      faq: [...(prev.faq || []), { question: '', answer: '' }]
    }));
  };

  const updateFaq = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      faq: prev.faq.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeFaq = (index) => {
    setSettings(prev => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index)
    }));
  };

  // Footer handlers
  const handleFooterChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      footer: { ...prev.footer, [field]: value }
    }));
  };

  const addQuickLink = () => {
    setSettings(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        quick_links: [...(prev.footer.quick_links || []), { id: `link-${Date.now()}`, label: '', url: '' }]
      }
    }));
  };

  const updateQuickLink = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        quick_links: prev.footer.quick_links.map((link, i) => 
          i === index ? { ...link, [field]: value } : link
        )
      }
    }));
  };

  const removeQuickLink = (index) => {
    setSettings(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        quick_links: prev.footer.quick_links.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Impostazioni salvate');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2">Impostazioni</h1>
          <p className="text-muted-foreground">Configura tutti i contenuti del sito</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Salvataggio...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Salva
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="hero">Hero</TabsTrigger>
          <TabsTrigger value="stats">Stats/Numeri</TabsTrigger>
          <TabsTrigger value="sections">Sezioni Home</TabsTrigger>
          <TabsTrigger value="about">Chi Sono</TabsTrigger>
          <TabsTrigger value="contact">Contatti</TabsTrigger>
          <TabsTrigger value="social">Social</TabsTrigger>
          <TabsTrigger value="footer">Footer</TabsTrigger>
          <TabsTrigger value="texts">Testi Globali</TabsTrigger>
          <TabsTrigger value="calendar">Calendario</TabsTrigger>
          <TabsTrigger value="faq">FAQ</TabsTrigger>
          <TabsTrigger value="legal">Privacy</TabsTrigger>
          <TabsTrigger value="maintenance">Manutenzione</TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero">
          <Card>
            <CardHeader>
              <CardTitle>Sezione Hero</CardTitle>
              <CardDescription>
                L'immagine principale e i testi della homepage
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ImageUploader
                  value={settings?.hero_image}
                  onChange={(data) => handleImageChange('hero_image', data)}
                  onRemove={() => handleImageChange('hero_image', null)}
                  label="Immagine Hero"
                  aspectRatio="21/9"
                  showUrlFallback={true}
                />
                
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block">Titolo</Label>
                    <Input
                      name="hero_title"
                      value={settings?.hero_title || ''}
                      onChange={handleInputChange}
                      placeholder="Andrea Caruso"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Sottotitolo</Label>
                    <Input
                      name="hero_subtitle"
                      value={settings?.hero_subtitle || ''}
                      onChange={handleInputChange}
                      placeholder="Fotografo & Videomaker"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Tagline</Label>
                    <Input
                      name="hero_tagline"
                      value={settings?.hero_tagline || ''}
                      onChange={handleInputChange}
                      placeholder="Catturare l'essenza, raccontare l'emozione."
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Testo Pulsante CTA</Label>
                  <Input
                    name="hero_cta_text"
                    value={settings?.hero_cta_text || ''}
                    onChange={handleInputChange}
                    placeholder="Prenota Ora"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Link CTA</Label>
                  <Input
                    name="hero_cta_link"
                    value={settings?.hero_cta_link || ''}
                    onChange={handleInputChange}
                    placeholder="/prenota"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Video Hero (opzionale)</Label>
                <div className="flex flex-wrap items-start gap-3 mb-2">
                  <VideoUploader
                    onUploaded={(v) => setSettings(prev => ({ ...prev, hero_video: v.url }))}
                    label="Carica Video Hero (MP4/MOV, max 300MB)"
                    testId="hero-video-upload-btn"
                  />
                  {settings?.hero_video && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setSettings(prev => ({ ...prev, hero_video: '' }))} data-testid="hero-video-remove-btn">
                      Rimuovi video
                    </Button>
                  )}
                </div>
                {settings?.hero_video && (
                  <video src={settings.hero_video} muted playsInline controls preload="metadata" className="w-full max-w-md aspect-video bg-black rounded-sm mb-2" data-testid="hero-video-preview" />
                )}
                <Input
                  name="hero_video"
                  value={settings?.hero_video || ''}
                  onChange={handleInputChange}
                  placeholder="oppure incolla un URL .mp4"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Se presente, il video (muto, in loop) verrà mostrato al posto dell'immagine. Consigliato: MP4 orizzontale, 15-30 secondi, sotto i 50MB per un caricamento veloce della home.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Section */}
        <TabsContent value="stats">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Statistiche / Numeri</CardTitle>
                  <CardDescription>
                    I numeri che mostri nella homepage (es. 500+, 10 anni, 100%)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={settings?.stats_section_visible !== false}
                      onCheckedChange={(v) => handleSwitchChange('stats_section_visible', v)}
                    />
                    <Label className="text-sm">Mostra sezione</Label>
                  </div>
                  <Button variant="outline" size="sm" onClick={addStat}>
                    <Plus size={14} className="mr-1" />
                    Aggiungi
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-2 block">Titolo Sezione</Label>
                <Input
                  name="stats_section_title"
                  value={settings?.stats_section_title || ''}
                  onChange={handleInputChange}
                  placeholder="In Numeri"
                />
              </div>

              <div>
                <Label className="mb-3 block">Statistiche (trascina per riordinare)</Label>
                <div className="grid grid-cols-3 gap-2 mb-2 px-10 text-xs text-muted-foreground">
                  <span>Valore</span>
                  <span>Suffisso</span>
                  <span>Etichetta</span>
                </div>
                
                {settings?.stats?.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleStatsDragEnd}
                  >
                    <SortableContext
                      items={settings.stats.map(s => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {settings.stats.map((stat) => (
                          <SortableStatItem
                            key={stat.id}
                            stat={stat}
                            onUpdate={updateStat}
                            onRemove={removeStat}
                            onToggleVisibility={toggleStatVisibility}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <p className="text-muted-foreground text-center py-8 border border-dashed border-border rounded-sm">
                    Nessuna statistica. Clicca "Aggiungi" per iniziare.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Home Sections Config */}
        <TabsContent value="sections">
          <Card>
            <CardHeader>
              <CardTitle>Configurazione Sezioni Home</CardTitle>
              <CardDescription>
                Attiva/disattiva e configura le sezioni della homepage
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Services Section */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Sezione Servizi</h4>
                  <Switch
                    checked={settings?.services_section_visible !== false}
                    onCheckedChange={(v) => handleSwitchChange('services_section_visible', v)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Sottotitolo</Label>
                    <Input
                      name="services_section_subtitle"
                      value={settings?.services_section_subtitle || ''}
                      onChange={handleInputChange}
                      placeholder="Cosa Faccio"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Titolo</Label>
                    <Input
                      name="services_section_title"
                      value={settings?.services_section_title || ''}
                      onChange={handleInputChange}
                      placeholder="Servizi"
                    />
                  </div>
                </div>
              </div>

              {/* Portfolio Section */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Sezione Portfolio</h4>
                  <Switch
                    checked={settings?.portfolio_section_visible !== false}
                    onCheckedChange={(v) => handleSwitchChange('portfolio_section_visible', v)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Sottotitolo</Label>
                    <Input
                      name="portfolio_section_subtitle"
                      value={settings?.portfolio_section_subtitle || ''}
                      onChange={handleInputChange}
                      placeholder="Portfolio"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Titolo</Label>
                    <Input
                      name="portfolio_section_title"
                      value={settings?.portfolio_section_title || ''}
                      onChange={handleInputChange}
                      placeholder="Lavori Selezionati"
                    />
                  </div>
                </div>
              </div>

              {/* Testimonials Section */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Sezione Testimonianze</h4>
                  <Switch
                    checked={settings?.testimonials_section_visible !== false}
                    onCheckedChange={(v) => handleSwitchChange('testimonials_section_visible', v)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Sottotitolo</Label>
                    <Input
                      name="testimonials_section_subtitle"
                      value={settings?.testimonials_section_subtitle || ''}
                      onChange={handleInputChange}
                      placeholder="Testimonianze"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Titolo</Label>
                    <Input
                      name="testimonials_section_title"
                      value={settings?.testimonials_section_title || ''}
                      onChange={handleInputChange}
                      placeholder="Cosa Dicono di Me"
                    />
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Sezione CTA Finale</h4>
                  <Switch
                    checked={settings?.cta_section_visible !== false}
                    onCheckedChange={(v) => handleSwitchChange('cta_section_visible', v)}
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block text-sm">Titolo</Label>
                    <Input
                      name="cta_title"
                      value={settings?.cta_title || ''}
                      onChange={handleInputChange}
                      placeholder="Pronto a raccontare la tua storia?"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Sottotitolo</Label>
                    <Textarea
                      name="cta_subtitle"
                      value={settings?.cta_subtitle || ''}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Ogni progetto è unico..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-2 block text-sm">Testo Pulsante</Label>
                      <Input
                        name="cta_button_text"
                        value={settings?.cta_button_text || ''}
                        onChange={handleInputChange}
                        placeholder="Prenota una Consulenza"
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm">Link Pulsante</Label>
                      <Input
                        name="cta_button_link"
                        value={settings?.cta_button_link || ''}
                        onChange={handleInputChange}
                        placeholder="/prenota"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* About Section */}
        <TabsContent value="about">
          <Card>
            <CardHeader>
              <CardTitle>Chi Sono</CardTitle>
              <CardDescription>
                La tua presentazione e foto profilo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ImageUploader
                  value={settings?.about_image}
                  onChange={(data) => handleImageChange('about_image', data)}
                  onRemove={() => handleImageChange('about_image', null)}
                  label="Foto Profilo / About"
                  aspectRatio="4/5"
                  showUrlFallback={true}
                />
                
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block">Titolo Sezione</Label>
                    <Input
                      name="about_title"
                      value={settings?.about_title || ''}
                      onChange={handleInputChange}
                      placeholder="Chi Sono"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Testo (Rich Text)</Label>
                    <RichTextEditor
                      value={settings?.about_text || ''}
                      onChange={(v) => handleRichTextChange('about_text', v)}
                      placeholder="Racconta di te, della tua esperienza, del tuo approccio..."
                      minHeight="200px"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Section */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>Informazioni di Contatto</CardTitle>
              <CardDescription>
                I tuoi recapiti mostrati nel sito
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Email</Label>
                  <Input
                    name="contact_email"
                    value={settings?.contact_email || ''}
                    onChange={handleInputChange}
                    type="email"
                    placeholder="info@tuodominio.it"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Telefono</Label>
                  <Input
                    name="contact_phone"
                    value={settings?.contact_phone || ''}
                    onChange={handleInputChange}
                    placeholder="+39 333 1234567"
                  />
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Indirizzo</Label>
                <Input
                  name="contact_address"
                  value={settings?.contact_address || ''}
                  onChange={handleInputChange}
                  placeholder="Via Roma 1, 20100 Milano"
                />
              </div>

              {/* Google Maps */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin size={18} />
                    <h4 className="font-medium">Google Maps</h4>
                  </div>
                  <Switch
                    checked={settings?.google_maps_enabled || false}
                    onCheckedChange={(v) => handleSwitchChange('google_maps_enabled', v)}
                  />
                </div>
                {settings?.google_maps_enabled && (
                  <div>
                    <Label className="mb-2 block text-sm">URL Embed Google Maps</Label>
                    <Input
                      name="google_maps_embed_url"
                      value={settings?.google_maps_embed_url || ''}
                      onChange={handleInputChange}
                      placeholder="https://www.google.com/maps/embed?pb=..."
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Vai su Google Maps → Cerca indirizzo → Condividi → Incorpora mappa → Copia URL src
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Section */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <CardTitle>Social Media</CardTitle>
              <CardDescription>
                I tuoi profili social
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Instagram</Label>
                  <Input
                    name="social_instagram"
                    value={settings?.social_instagram || ''}
                    onChange={handleInputChange}
                    placeholder="https://instagram.com/tuousername"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Facebook</Label>
                  <Input
                    name="social_facebook"
                    value={settings?.social_facebook || ''}
                    onChange={handleInputChange}
                    placeholder="https://facebook.com/tuapagina"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">YouTube</Label>
                  <Input
                    name="social_youtube"
                    value={settings?.social_youtube || ''}
                    onChange={handleInputChange}
                    placeholder="https://youtube.com/@tuocanale"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Vimeo</Label>
                  <Input
                    name="social_vimeo"
                    value={settings?.social_vimeo || ''}
                    onChange={handleInputChange}
                    placeholder="https://vimeo.com/tuocanale"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">TikTok</Label>
                  <Input
                    name="social_tiktok"
                    value={settings?.social_tiktok || ''}
                    onChange={handleInputChange}
                    placeholder="https://tiktok.com/@tuousername"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">LinkedIn</Label>
                  <Input
                    name="social_linkedin"
                    value={settings?.social_linkedin || ''}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/tuoprofilo"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Footer Section */}
        <TabsContent value="footer">
          <Card>
            <CardHeader>
              <CardTitle>Footer</CardTitle>
              <CardDescription>
                Configura il contenuto del footer del sito
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-2 block">Testo Copyright</Label>
                <Input
                  value={settings?.footer?.copyright_text || ''}
                  onChange={(e) => handleFooterChange('copyright_text', e.target.value)}
                  placeholder="© {year} Andrea Caruso. Tutti i diritti riservati."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Usa {'{year}'} per inserire l'anno corrente automaticamente
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings?.footer?.show_social !== false}
                    onCheckedChange={(v) => handleFooterChange('show_social', v)}
                  />
                  <Label>Mostra Social</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings?.footer?.show_contact_info !== false}
                    onCheckedChange={(v) => handleFooterChange('show_contact_info', v)}
                  />
                  <Label>Mostra Contatti</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings?.footer?.show_quick_links !== false}
                    onCheckedChange={(v) => handleFooterChange('show_quick_links', v)}
                  />
                  <Label>Mostra Link Rapidi</Label>
                </div>
              </div>

              {/* Quick Links */}
              {settings?.footer?.show_quick_links !== false && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Link Rapidi</Label>
                    <Button variant="outline" size="sm" onClick={addQuickLink}>
                      <Plus size={14} className="mr-1" />
                      Aggiungi Link
                    </Button>
                  </div>
                  
                  {settings?.footer?.quick_links?.length > 0 ? (
                    <div className="space-y-2">
                      {settings.footer.quick_links.map((link, index) => (
                        <div key={link.id || index} className="flex items-center gap-2">
                          <Input
                            value={link.label}
                            onChange={(e) => updateQuickLink(index, 'label', e.target.value)}
                            placeholder="Etichetta"
                            className="flex-1"
                          />
                          <Input
                            value={link.url}
                            onChange={(e) => updateQuickLink(index, 'url', e.target.value)}
                            placeholder="/pagina"
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeQuickLink(index)}
                            className="text-destructive"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm text-center py-4 border border-dashed rounded-sm">
                      Nessun link rapido configurato
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="mb-2 block">Testo Aggiuntivo (opzionale)</Label>
                <Textarea
                  value={settings?.footer?.additional_text || ''}
                  onChange={(e) => handleFooterChange('additional_text', e.target.value)}
                  rows={2}
                  placeholder="P.IVA, info aggiuntive..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAQ Section */}
        <TabsContent value="faq">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>FAQ</CardTitle>
                <CardDescription>
                  Domande frequenti mostrate nella pagina FAQ
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={addFaq}>
                <Plus size={14} className="mr-1" />
                Aggiungi FAQ
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.faq?.map((item, index) => (
                <div key={index} className="border border-border p-4 rounded-sm space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <Input
                        value={item.question}
                        onChange={(e) => updateFaq(index, 'question', e.target.value)}
                        placeholder="Domanda"
                      />
                      <Textarea
                        value={item.answer}
                        onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                        placeholder="Risposta"
                        rows={3}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFaq(index)}
                      className="text-destructive shrink-0"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
              {(!settings?.faq || settings.faq.length === 0) && (
                <p className="text-muted-foreground text-center py-8">
                  Nessuna FAQ. Clicca "Aggiungi FAQ" per iniziare.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Legal Section */}
        <TabsContent value="legal">
          <Card>
            <CardHeader>
              <CardTitle>Privacy e Cookie Policy</CardTitle>
              <CardDescription>
                Testi legali con formattazione rich text
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-2 block">Privacy Policy</Label>
                <RichTextEditor
                  value={settings?.privacy_policy || ''}
                  onChange={(v) => handleRichTextChange('privacy_policy', v)}
                  placeholder="Informativa sulla Privacy..."
                  minHeight="250px"
                />
              </div>
              <div>
                <Label className="mb-2 block">Cookie Policy</Label>
                <RichTextEditor
                  value={settings?.cookie_policy || ''}
                  onChange={(v) => handleRichTextChange('cookie_policy', v)}
                  placeholder="Questo sito utilizza cookie..."
                  minHeight="150px"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Global Texts / Microcopy Section */}
        <TabsContent value="texts">
          <Card>
            <CardHeader>
              <CardTitle>Testi Globali / Microcopy</CardTitle>
              <CardDescription>
                Tutti i testi modificabili delle pagine: bottoni, placeholder, messaggi
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Page Titles */}
              <div>
                <h4 className="font-medium mb-4 border-b pb-2">Titoli Pagine</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Contatti - Titolo</Label>
                    <Input
                      value={settings?.texts?.page_contact_title || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, page_contact_title: e.target.value }
                      }))}
                      placeholder="Parliamo"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Contatti - Sottotitolo</Label>
                    <Input
                      value={settings?.texts?.page_contact_subtitle || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, page_contact_subtitle: e.target.value }
                      }))}
                      placeholder="Hai un progetto in mente?"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Prenotazione - Titolo</Label>
                    <Input
                      value={settings?.texts?.page_booking_title || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, page_booking_title: e.target.value }
                      }))}
                      placeholder="Prenota"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Recensioni - Titolo</Label>
                    <Input
                      value={settings?.texts?.page_reviews_title || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, page_reviews_title: e.target.value }
                      }))}
                      placeholder="Recensioni"
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div>
                <h4 className="font-medium mb-4 border-b pb-2">Testi Bottoni</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Prenota Ora</Label>
                    <Input
                      value={settings?.texts?.btn_book_now || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, btn_book_now: e.target.value }
                      }))}
                      placeholder="Prenota Ora"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Contattami</Label>
                    <Input
                      value={settings?.texts?.btn_contact || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, btn_contact: e.target.value }
                      }))}
                      placeholder="Contattami"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Invia</Label>
                    <Input
                      value={settings?.texts?.btn_send || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, btn_send: e.target.value }
                      }))}
                      placeholder="Invia"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Paga Caparra</Label>
                    <Input
                      value={settings?.texts?.btn_pay_deposit || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, btn_pay_deposit: e.target.value }
                      }))}
                      placeholder="Paga Caparra"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Iscriviti Newsletter</Label>
                    <Input
                      value={settings?.texts?.btn_subscribe || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, btn_subscribe: e.target.value }
                      }))}
                      placeholder="Iscriviti"
                    />
                  </div>
                </div>
              </div>

              {/* Form Labels */}
              <div>
                <h4 className="font-medium mb-4 border-b pb-2">Etichette Form</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Nome</Label>
                    <Input
                      value={settings?.texts?.form_name || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, form_name: e.target.value }
                      }))}
                      placeholder="Nome"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Email</Label>
                    <Input
                      value={settings?.texts?.form_email || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, form_email: e.target.value }
                      }))}
                      placeholder="Email"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Telefono</Label>
                    <Input
                      value={settings?.texts?.form_phone || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, form_phone: e.target.value }
                      }))}
                      placeholder="Telefono"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Messaggio</Label>
                    <Input
                      value={settings?.texts?.form_message || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, form_message: e.target.value }
                      }))}
                      placeholder="Messaggio"
                    />
                  </div>
                </div>
              </div>

              {/* Success Messages */}
              <div>
                <h4 className="font-medium mb-4 border-b pb-2">Messaggi di Successo</h4>
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block text-sm">Contatto inviato</Label>
                    <Textarea
                      value={settings?.texts?.msg_contact_success || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, msg_contact_success: e.target.value }
                      }))}
                      placeholder="Grazie per averci contattato!"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Prenotazione inviata</Label>
                    <Textarea
                      value={settings?.texts?.msg_booking_success || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, msg_booking_success: e.target.value }
                      }))}
                      placeholder="Richiesta di prenotazione inviata!"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Pagamento completato</Label>
                    <Textarea
                      value={settings?.texts?.msg_payment_success || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, msg_payment_success: e.target.value }
                      }))}
                      placeholder="Pagamento completato con successo!"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Newsletter */}
              <div>
                <h4 className="font-medium mb-4 border-b pb-2">Newsletter</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Titolo</Label>
                    <Input
                      value={settings?.texts?.newsletter_title || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, newsletter_title: e.target.value }
                      }))}
                      placeholder="Resta Aggiornato"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm">Sottotitolo</Label>
                    <Input
                      value={settings?.texts?.newsletter_subtitle || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        texts: { ...prev.texts, newsletter_subtitle: e.target.value }
                      }))}
                      placeholder="Iscriviti e ricevi il 15% di sconto"
                    />
                  </div>
                </div>
              </div>

              {/* Cancellation Policy */}
              <div>
                <h4 className="font-medium mb-4 border-b pb-2">Politica di Cancellazione</h4>
                <div>
                  <Label className="mb-2 block text-sm">Testo Completo</Label>
                  <RichTextEditor
                    value={settings?.texts?.cancellation_policy_text || ''}
                    onChange={(v) => setSettings(prev => ({
                      ...prev,
                      texts: { ...prev.texts, cancellation_policy_text: v }
                    }))}
                    placeholder="La cancellazione è sempre possibile..."
                    minHeight="150px"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Settings Section */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader>
              <CardTitle>Impostazioni Calendario</CardTitle>
              <CardDescription>
                Configura la modalità di prenotazione e la caparra
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium">Modalità Calendario</h4>
                    <p className="text-sm text-muted-foreground">
                      {settings?.calendar_mode === 'auto' 
                        ? 'AUTO: Le prenotazioni vengono confermate automaticamente'
                        : 'MANUALE: Le prenotazioni richiedono approvazione'}
                    </p>
                  </div>
                  <Switch
                    checked={settings?.calendar_mode === 'auto'}
                    onCheckedChange={(v) => handleSwitchChange('calendar_mode', v ? 'auto' : 'manual')}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  <strong>AUTO:</strong> Slot bloccato immediatamente, cliente può pagare subito<br/>
                  <strong>MANUALE:</strong> Slot in stato "richiesto", pagamento solo dopo approvazione
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Percentuale Caparra (%)</Label>
                <Input
                  type="number"
                  min="5"
                  max="50"
                  value={settings?.deposit_percentage || 15}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    deposit_percentage: parseFloat(e.target.value) || 15
                  }))}
                  placeholder="15"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Percentuale del costo del servizio richiesta come caparra
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Email Notifiche</Label>
                <Input
                  type="email"
                  value={settings?.notification_email || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    notification_email: e.target.value
                  }))}
                  placeholder="info@andreacaruso.art"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Email dove ricevere tutte le notifiche (prenotazioni, pagamenti, contatti)
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Maintenance Section */}
        <TabsContent value="maintenance">
          <MaintenanceSection />
        </TabsContent>
      </Tabs>

      {/* Floating Save Button on mobile */}
      <div className="fixed bottom-6 right-6 lg:hidden">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          size="lg"
          className="shadow-lg"
        >
          {saving ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <Save size={20} />
          )}
        </Button>
      </div>
    </div>
  );
}
